import React, { useEffect, useState } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
	Area,
	AreaChart,
} from 'recharts';
import moment from 'moment';
import { analyticsAPI } from '../../../services/constants';
import { request } from '../../../services/utilities';
import TableLoading from '../../../components/TableLoading';
import SlidingPane from '../../../components/SlidingPane';
import { Card, Col, Row, Statistic } from 'antd';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';
import { TimeRangeDropdownAlt3 as TimeRangeDropdown } from '../../../components/TimeRangeDropdownAlt3';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff6e54', '#ffc0cb', '#faad14'];

// const TimeRangeDropdown = ({ value, onChange, chartId }) => {
// 	return (
// 		<select
// 			value={value}
// 			onChange={e => onChange(e.target.value, chartId)}
// 			className="form-control form-control-sm rounded"
// 			style={{ width: '160px', marginBottom: '10px', borderRadius: '50px' }}
// 		>
// 			<option value="today">Today</option>
// 			<option value="pastWeek">Past Week</option>
// 			<option value="past30Days">Past 30 Days</option>
// 			<option value="lastMonth">Last Month</option>
// 			<option value="pastQuarter">Past Quarter</option>
// 			<option value="pastSixMonths">Past Six Months</option>
// 			<option value="pastYear">Past Year</option>
// 			<option value="lastYear">Last year</option>
// 			<option value="pastThreeYears">Past Three Years</option>
// 			<option value="allTime">All Time</option>
// 		</select>
// 	);
// };

const StaffCafeteriaOverview = ({ selectedStaff }) => {
	const [timeRanges, setTimeRanges] = useState({
		overviewMetrics: 'past30Days',
		amountItem: 'past30Days',
		orderAmount: 'past30Days',
		orderRank: 'past30Days',
		orderTrends: 'past30Days',
		orderCount: 'past30Days',
		orderTime: 'past30Days',
		orderTrendsAmount: 'past30Days',
		orderCustomerType: 'all-time',
		orderReadyBy: 'past30Days',
		orderCategory: 'past30Days',
		orderDescription: 'past30Days',
	});

	const mapTimeRangeToApiFilter = range => {
		switch (range) {
			case 'today':
				return 'today';
			case 'pastWeek':
				return 'past-week';
			case 'past30Days':
				return 'past-month';
			case 'lastMonth':
				return 'last-month';
			case 'pastQuarter':
				return 'past-quarter';
			case 'pastSixMonths':
				return 'past-six-months';
			case 'pastYear':
				return 'past-year';
			case 'lastYear':
				return 'last-year';
			case 'pastThreeYears':
				return 'past-three-years';
			case 'allTime':
				return 'all-time';
			default:
				return 'past-month';
		}
	};

	const [loadingStates, setLoadingStates] = useState({
		overviewMetrics: false,
		amountItem: false,
		orderAmount: false,
		orderRank: false,
		orderTrends: false,
		orderCount: false,
		orderTime: false,
		orderTrendsAmount: false,
		orderCustomerType: false,
		orderReadyBy: false,
		orderCategory: false,
		orderDescription: false,
	});

	const [amountItem, setAmountItem] = useState([]);
	const [orderAmount, setOrderAmount] = useState([]);
	const [orderRank, setOrderRank] = useState([]);
	const [orderTrends, setOrderTrends] = useState([]);
	const [orderCount, setOrderCount] = useState([]);
	const [orderTime, setOrderTime] = useState('');
	const [orderTrendsAmount, setOrderTrendsAmount] = useState([]);
	const [orderCustomerType, setOrderCustomerType] = useState('');
	const [orderReadyBy, setOrderReadyBy] = useState('');
	const [orderCategory, setOrderCategory] = useState('');
	const [orderDescription, setOrderDescription] = useState('');
	const [modal, setModal] = useState(false);

	// Api calls
	const fetchAmountItem = async range => {
		setLoadingStates(prev => ({ ...prev, amountItem: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`${analyticsAPI}/cafeteria-amount-item?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponseTop = res?.result
				?.sort(() => Math.random() - 0.5)
				?.map(x => ({
					name: x?.name,
					count: parseInt(x?.totalEarned, 10),
				}))
				.slice(0, 20);
			setAmountItem(apiResponseTop);
			console.log('Amount Item:', apiResponseTop);
		} catch (error) {
			console.error('Failed to fetch amountItem:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, amountItem: false }));
		}
	};

	const fetchOrderAmount = async range => {
		setLoadingStates(prev => ({ ...prev, orderAmount: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(
				`${analyticsAPI}/cafeteria-order-amount?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			setOrderAmount(res?.result?.total);
		} catch (error) {
			console.error('Failed to fetch orderAmount:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderAmount: false }));
		}
	};

	const fetchOrderRank = async range => {
		setLoadingStates(prev => ({ ...prev, orderRank: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`${analyticsAPI}/cafeteria-order-rank?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);

			const apiResponse = res?.result?.map(x => ({
				name: x?.name,
				count: parseInt(x?.orderCount, 10),
			}));
			setOrderRank(apiResponse);
		} catch (error) {
			console.error('Failed to fetch orderRank:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderRank: false }));
		}
	};

	const fetchOrderTrends = async range => {
		setLoadingStates(prev => ({ ...prev, orderTrend: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(
				`${analyticsAPI}/cafeteria-order-trends?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,

				count: parseInt(x?.count, 10) || 0,
			}));
			setOrderTrends(apiResponse);
		} catch (error) {
			console.error('Failed to fetch orderTrend:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderTrend: false }));
		}
	};

	const fetchOrderCount = async range => {
		setLoadingStates(prev => ({ ...prev, orderCount: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`${analyticsAPI}/cafeteria-order-count?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			setOrderCount(res?.result);
		} catch (error) {
			console.error('Failed to fetch orderCount:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderCount: false }));
		}
	};

	const fetchOrderTime = async range => {
		setLoadingStates(prev => ({ ...prev, orderTime: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(`${analyticsAPI}/cafeteria-order-time?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const [key, value] = Object.entries(res?.result)[0];

			setOrderTime(Math.round(parseFloat(key ? value : 0)) + ' min');
		} catch (error) {
			console.error('Failed to fetch orderTime:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderTime: false }));
		}
	};

	const fetchOrderTrendsAmount = async range => {
		setLoadingStates(prev => ({ ...prev, orderTrendsAmount: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(
				`${analyticsAPI}/cafeteria-order-trends-amount?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			const apiResponse = res?.result?.map(x => ({
				name: x?.date || x?.day || x?.week || (x?.month && moment(x?.month).format('MMM')) || x?.year || x?.hour,
				count: parseInt(x?.total_earned, 10),
			}));
			setOrderTrendsAmount(apiResponse);
		} catch (error) {
			console.error('Failed to fetch orderTrendsAmount:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderTrendsAmount: false }));
		}
	};

	const fetchOrderCustomerType = async range => {
		setLoadingStates(prev => ({ ...prev, orderCustomerType: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(
				`${analyticsAPI}/cafeteria-order-customer-type?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			setOrderCustomerType(res?.result);
		} catch (error) {
			console.error('Failed to fetch orderCustomerType:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderCustomerType: false }));
		}
	};

	const fetchOrderReadyBy = async range => {
		setLoadingStates(prev => ({ ...prev, orderReadyBy: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(
				`${analyticsAPI}/cafeteria-order-ready-by?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			setOrderReadyBy(res?.result);
		} catch (error) {
			console.error('Failed to fetch orderReadyBy:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderReadyBy: false }));
		}
	};

	const fetchOrderCategory = async range => {
		setLoadingStates(prev => ({ ...prev, orderCategory: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(
				`${analyticsAPI}/cafeteria-order-category?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			setOrderCategory(res?.result);
		} catch (error) {
			console.error('Failed to fetch orderCategory:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderCategory: false }));
		}
	};

	const fetchOrderDescription = async range => {
		setLoadingStates(prev => ({ ...prev, orderDescription: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const res = await request(
				`${analyticsAPI}/cafeteria-order-description?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			const apiResponse = res?.result
				// ?.sort((a, b) => b.total - a.total)
				?.sort(() => Math.random() - 0.5)
				?.map(x => ({
					description: x.description,
					total_earned: parseInt(x.total_earned, 10),
					total_orders: parseInt(x.total_orders, 10),
				}));
			setOrderDescription(apiResponse);
			console.log('Order Description:', apiResponse);
		} catch (error) {
			console.error('Failed to fetch orderDescription:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, orderDescription: false }));
		}
	};

	//initial data load
	useEffect(() => {
		fetchAmountItem(timeRanges.amountItem);
		fetchOrderAmount(timeRanges.orderAmount);
		fetchOrderRank(timeRanges.orderRank);
		fetchOrderTrends(timeRanges.orderTrends);
		fetchOrderCount(timeRanges.orderCount);
		fetchOrderTime(timeRanges.orderTime);
		fetchOrderTrendsAmount(timeRanges.orderTrendsAmount);
		fetchOrderCustomerType(timeRanges.orderCustomerType);
		fetchOrderReadyBy(timeRanges.orderReadyBy);
		fetchOrderCategory(timeRanges.orderCategory);
		fetchOrderDescription(timeRanges.orderDescription);
	}, []);

	//handle time range changes
	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'amountItem':
				fetchAmountItem(range);
				break;
			case 'orderRank':
				fetchOrderRank(range);
				break;
			case 'orderTrends':
				fetchOrderTrends(range);
				break;
			case 'orderTrendsAmount':
				fetchOrderTrendsAmount(range);
				break;
			case 'orderCustomerType':
				fetchOrderCustomerType(range);
				break;
			case 'orderReadyBy':
				fetchOrderReadyBy(range);
				break;
			case 'orderCategory':
				fetchOrderCategory(range);
				break;
			case 'orderDescription':
				fetchOrderDescription(range);
				break;
			case 'overviewMetrics':
				fetchOrderAmount(range);
				fetchOrderCount(range);
				fetchOrderTime(range);
				break;
			default:
				break;
		}
	};

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	return (
		<div className="content-i">
			<div className="content-box">
				<div className="row">
					{/* Overview Metrics */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Overview</h6>
								<TimeRangeDropdown value={timeRanges.overviewMetrics} onChange={handleTimeRangeChange} chartId="overviewMetrics" />
							</div>
							<p className="chart-description mb-3 p-2 card">Summary metrics showing overall cafeteria activity.</p>
							<div className="element-box">
								<div className="row">
									<div className="col-md-2 col-sm-4 col-lg-3">
										<div className="value text-primary" style={{ fontSize: '25px' }}>
											{orderAmount.toLocaleString() || 0}
										</div>
										<div className="label">Total Order Amount</div>
									</div>
									<div className="col-md-2 col-sm-4 col-lg-3">
										<div className="value text-success" style={{ fontSize: '25px' }}>
											{orderCount.toLocaleString() || 0}
										</div>
										<div className="label"> Total Order Count</div>
									</div>
									<div className="col-md-2 col-sm-4 col-lg-3">
										<div className="value text-warning" style={{ fontSize: '25px' }}>
											{orderTime}
										</div>
										<div className="label">Average Ready Time Minutes</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Cafeteria Amount Items */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Amount Item</h6>
								<TimeRangeDropdown value={timeRanges.amountItem} onChange={handleTimeRangeChange} chartId="amountItem" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Displays the total monetary value of all individual items sold within the selected time frame. This helps you understand
								which days or periods generate the highest item-level revenue.
							</p>
							<div className="element-box">
								{loadingStates.amountItem ? (
									<TableLoading />
								) : amountItem.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={amountItem} margin={{ top: 20, right: 20, left: 10, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Cafeteria Order Rank top */}
					<div className="col-sm-6">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Order Rank (Top)</h6>
								<TimeRangeDropdown value={timeRanges.orderRank} onChange={handleTimeRangeChange} chartId="orderRank" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Highlights the highest performing items based on order frequency. Use this to identify customer preferences and items that
								may need promotion or removal.
							</p>
							<div className="element-box">
								{loadingStates.orderRank ? (
									<TableLoading />
								) : orderRank?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<LineChart data={orderRank?.slice(0, 10)} margin={{ top: 20, right: 20, left: 10, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '10px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Cafeteria Order Rank Bottom*/}
					<div className="col-sm-6">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Order Rank (Bottom)</h6>
								<div className="d-flex justify-content-end w-100">
									<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											onClick={() => setModal(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
									&nbsp;
									<TimeRangeDropdown value={timeRanges.orderRank} onChange={handleTimeRangeChange} chartId="orderRank" />
								</div>
							</div>
							<p className="chart-description mb-3 p-2 card">
								Highlights the lowest performing items based on order frequency. Use this to identify customer preferences and items that
								may need promotion or removal.
							</p>
							<div className="element-box">
								{loadingStates.orderRank ? (
									<TableLoading />
								) : orderRank.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<LineChart data={orderRank?.slice(-10)} margin={{ top: 20, right: 20, left: 10, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '10px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
						<SlidingPane isOpen={modal}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModal(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdown value={timeRanges.orderRank} onChange={handleTimeRangeChange} chartId="orderRank" />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Cafeteria Order Rank </h6>

											<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												{/* <TableLoading /> */}

												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total" value={orderRank.length} suffix="" />
															<small className="text-muted">Total Items</small>
														</Card>
													</Col>
													{/* <Col span={8}>
														<Card>
															<Statistic
																title="Total Revenue"
																value={transSummary?.totalRevenueFormatted} suffix=""
															/>

															<small className="text-muted">Cumulative Revenue From --- to --</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total Transactions"
																value={transSummary?.totalTransactions} suffix=""
															/>
															<Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/>
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col> */}
													{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										This chart highlights customer order based on order frequency. Use this to identify customer preferences and items that
										may need promotion or removal.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loadingStates.orderRank ? (
															<TableLoading />
														) : orderRank.length > 0 ? (
															<div style={{ height: 'auto' }}>
																{orderRank.length > 45 ? (
																	<ResponsiveContainer width="100%" height={orderRank?.slice(-10)?.length * 40}>
																		<LineChart
																			data={orderRank?.slice(-10)}
																			layout="vertical"
																			margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
																		>
																			<CartesianGrid strokeDasharray="3 3" />

																			{/* Categories on Y axis */}
																			<YAxis
																				type="category"
																				dataKey="name"
																				tick={{ fontSize: 12 }}
																				width={120}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>

																			{/* Values on X axis */}
																			<XAxis type="number" domain={[0, 'auto']} />

																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>
																			<Legend />

																			<Line
																				type="monotone"
																				dataKey="count"
																				stroke="#22BB33"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					position="right" // 👈 switch from "top" to "right" for vertical layout
																					content={renderCustomLabelAlt}
																					style={{ fontSize: '10px', fill: '#333' }}
																				/>
																			</Line>
																		</LineChart>
																	</ResponsiveContainer>
																) : (
																	<ResponsiveContainer>
																		<LineChart data={orderRank?.slice(-10)} margin={{ top: 20, right: 20, left: 10, bottom: 50 }}>
																			<CartesianGrid strokeDasharray="3 3" />
																			<XAxis
																				dataKey={'name'}
																				angle={-45}
																				textAnchor="end"
																				fontSize={12}
																				interval={0}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>
																			<YAxis domain={[0, 'auto']} />
																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>

																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																			<Line
																				type="monotone"
																				dataKey="count"
																				stroke="#22BB33"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					content={renderCustomLabelAlt}
																					position="top"
																					style={{ fontSize: '10px', fill: '#333' }}
																				/>
																			</Line>
																		</LineChart>
																	</ResponsiveContainer>
																)}
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/* Cafeteria Order Trends */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Order Trends</h6>
								<TimeRangeDropdown value={timeRanges.orderTrends} onChange={handleTimeRangeChange} chartId="orderTrends" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Visualizes the pattern of order activity over time — showing increases or drops in customer demand. Ideal for spotting peak
								sales hours, days, or seasonal shifts in cafeteria usage
							</p>
							<div className="element-box">
								{loadingStates.orderTrends ? (
									<TableLoading />
								) : orderTrends.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={orderTrends} margin={{ top: 20, right: 20, left: 10, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.orderTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Cafeteria Order Trends Amount*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header"> Order Trends Amount</h6>
								<TimeRangeDropdown value={timeRanges.orderTrendsAmount} onChange={handleTimeRangeChange} chartId="orderTrendsAmount" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Shows how the total value of orders changes over time — daily, weekly, or monthly. Useful for revenue forecasting and
								identifying underperforming periods.
							</p>
							<div className="element-box">
								{loadingStates.orderTrendsAmount ? (
									<TableLoading />
								) : orderTrendsAmount.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={orderTrendsAmount} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Legend wrapperStyle={{ marginBottom: '-50px' }} />

												<Bar dataKey="count" fill="#0088FE" name="count">
													<LabelList dataKey="count" content={formatBarLabel} position="top" fontSize={10} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Order Ready By*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Order Ready By </h6>
								<TimeRangeDropdown value={timeRanges.orderReadyBy} onChange={handleTimeRangeChange} chartId="orderReadyBy" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Captures the timestamp or average duration when orders were marked 'ready' by the kitchen. Ideal for measuring order
								turnaround time and meeting customer expectations.
							</p>
							<div className="element-box">
								{loadingStates.orderReadyBy ? (
									<TableLoading />
								) : orderReadyBy.length > 0 ? (
									<div style={{ height: 'auto' }}>
										<ResponsiveContainer height={orderReadyBy?.length * 30}>
											<BarChart layout="vertical" data={orderReadyBy} margin={{ top: 20, right: 40, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />

												<XAxis type="number" tickFormatter={value => `₦${value.toLocaleString()}`} />
												<YAxis interval={0} style={{ textTransform: 'capitalize' }} dataKey="ready_by" type="category" width={120} />

												<Tooltip formatter={value => `₦${value.toLocaleString()}`} labelFormatter={label => `Staff: ${label}`} />
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Bar dataKey="total_earned" fill="#8884d8" name="Total Earned">
													<LabelList dataKey="total_earned" position="right" formatter={value => `₦${value.toLocaleString()}`} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Order Category*/}
					<div className="col-sm-12 col-md-6">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Order Category </h6>
								<TimeRangeDropdown value={timeRanges.orderCategory} onChange={handleTimeRangeChange} chartId="orderCategory" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Displays total orders and earnings by cafeteria category to highlight top-performing meal types.
							</p>
							<div className="element-box">
								{loadingStates.orderCategory ? (
									<TableLoading />
								) : orderCategory.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<PieChart>
												<Pie
													data={orderCategory}
													dataKey="total_earned"
													nameKey="category"
													cx="50%"
													cy="50%"
													outerRadius={120}
													label={({ category, percent }) => `${category}: ${(percent * 100).toFixed(1)}%`}
												>
													{orderCategory.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList dataKey="total_earned" position="outside" formatter={value => `₦${value.toLocaleString()}`} />
												</Pie>
												<Tooltip formatter={value => `₦${value.toLocaleString()}`} />
												<Legend wrapperStyle={{ marginBottom: '-20px' }} />
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Cafeteria Order Order Customer type*/}
					<div className="col-sm-12 col-md-6">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header"> Order Customer Type</h6>
								<TimeRangeDropdown value={timeRanges.orderCustomerType} onChange={handleTimeRangeChange} chartId="orderCustomerType" />
							</div>
							<p className="chart-description mb-3 p-2 card">Breaks down customers into categories like patients, walk-in and staff.</p>
							<div className="element-box">
								{loadingStates.orderCustomerType ? (
									<TableLoading />
								) : orderCustomerType.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<PieChart>
												<Pie
													data={orderCustomerType}
													dataKey="total_earned"
													nameKey="customer"
													cx="50%"
													cy="50%"
													outerRadius={120}
													label={({ customer, percent }) => `${customer} ${(percent * 100).toFixed(1)}%`}
												>
													{orderCustomerType.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
												</Pie>
												<Tooltip formatter={value => `₦${value.toLocaleString()}`} />
												<Legend wrapperStyle={{ marginBottom: '-20px' }} />
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Order description*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Cafeteria Order Description </h6>
								<TimeRangeDropdown value={timeRanges.orderDescription} onChange={handleTimeRangeChange} chartId="orderDescription" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Breakdown of cafeteria earnings by meal description, highlighting how different food categories contribute to total revenue
								and order volume.
							</p>
							<div className="element-box">
								{loadingStates.orderDescription ? (
									<TableLoading />
								) : orderDescription.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<LineChart data={orderDescription} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="description"
													angle={-45}
													textAnchor="end"
													interval={0}
													tickFormatter={value => (value.length > 10 ? value.substring(0, 10) + '...' : value)}
													fontSize={12}
												/>
												<YAxis yAxisId="left" domain={[0, 'auto']} tickFormatter={val => `₦${(val / 1_000_000).toFixed(1)}M`} />
												<YAxis
													yAxisId="right"
													domain={[0, 'auto']}
													orientation="right"
													tickFormatter={val => `${(val / 1_000).toFixed(0)}k`}
												/>
												<Tooltip
													formatter={(value, name) =>
														name === 'Total Earned' ? `₦${value.toLocaleString()}` : `${value.toLocaleString()} orders`
													}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />

												{/* Line for total_earned */}
												<Line
													yAxisId="left"
													type="monotone"
													dataKey="total_earned"
													name="Total Earned"
													stroke="#8884d8"
													strokeWidth={2}
													dot={{ r: 4 }}
												>
													<LabelList dataKey="total_earned" position="top" formatter={val => `₦${(val / 1_000_000).toFixed(1)}M`} />
												</Line>

												{/* Line for total_orders */}
												<Line
													yAxisId="right"
													type="monotone"
													dataKey="total_orders"
													name="Total Orders"
													stroke="#82ca9d"
													strokeWidth={2}
													dot={{ r: 4 }}
												>
													<LabelList dataKey="total_orders" position="top" formatter={val => `${(val / 1000).toFixed(1)}k`} />
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default StaffCafeteriaOverview;
